import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { type Conversation, type RecentConversationItem } from '@jabberr/core';
import { api } from '../../lib/api.js';
import { signOut } from '../../lib/auth.js';
import { conversationTitle, initials, relativeTime } from '../../lib/format.js';
import {
  NAV_GROUP_LABELS,
  groupProjects,
  inNav,
  matchesQuery,
  projectCode,
  type NavGroup,
  type NavProject,
} from '../../lib/nav.js';
import { preferences, setPreferences, usePreference } from '../../lib/preferences.js';
import { cycleTheme, getNextTheme, getTheme, getThemeLabel } from '../../lib/theme.js';
import { useSession } from '../../state/session.js';
import { useCompanion } from '../../state/companion.js';
import { useMentions } from '../../state/mentions.js';
import { NewIcon } from '../NewIcon.js';
import { PersonalAppearance } from '../AppearancePicker.js';
import { WhatsNew } from '../WhatsNew.js';
import {
  BookIcon,
  BulbIcon,
  ChevronIcon,
  CogIcon,
  FileIcon,
  FolderIcon,
  KebabSmallIcon,
  PeopleIcon,
  SignOutIcon,
  StarIcon,
  SunIcon,
  MoonIcon,
  SystemIcon,
  TeamIcon,
  TrainIcon,
} from '../icons.js';
import { SidebarSearch } from './SidebarSearch.js';
import { isUnmentioned, type ProjectThreads, type ThreadRow } from './useProjectThreads.js';

interface SidebarProps {
  projectId: string | undefined;
  open: boolean;
  onMobile: boolean;
  openConversationId: string | undefined;
  creating: boolean;
  onNewConversation: (pid?: string) => Promise<void>;
  closeSidebar: () => void;
  threadsApi: ProjectThreads;
}

type SideProject = NavProject & { id: string; name: string };

/** Pinned projects for this person: server preferences, cached in this browser (lib/preferences.ts). */
function usePinned(): { pinned: string[]; toggle: (id: string) => void } {
  const pinned = usePreference('pinnedProjects') ?? [];
  const toggle = useCallback(
    (id: string) => {
      const now = preferences().pinnedProjects ?? [];
      setPreferences({ pinnedProjects: now.includes(id) ? now.filter((x) => x !== id) : [...now, id] });
    },
    [],
  );
  return { pinned, toggle };
}

/** Folded groups, remembered the same way. */
function useCollapsed(): { collapsed: Set<NavGroup>; toggle: (g: NavGroup) => void } {
  const list = usePreference('collapsedNavGroups');
  const collapsed = useMemo(() => new Set(list ?? []), [list]);
  const toggle = useCallback((g: NavGroup) => {
    const now = preferences().collapsedNavGroups ?? [];
    setPreferences({ collapsedNavGroups: now.includes(g) ? now.filter((x) => x !== g) : [...now, g] });
  }, []);
  return { collapsed, toggle };
}

/** Open ideas in the parking lot the person can see (Someday and Next up), re-read as they move around. */
function useParkingCount(userId: string | undefined, pathname: string): number | null {
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    api.parkingLot
      .count()
      .then(({ open }) => alive && setCount(open))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [userId, pathname]);
  return count;
}

/** Recent threads across projects, re-read when the person moves around the app. */
function useRecent(userId: string | undefined, pathname: string): RecentConversationItem[] {
  const [recent, setRecent] = useState<RecentConversationItem[]>([]);
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    api
      .recentConversations(5)
      .then(({ conversations }) => alive && setRecent(conversations))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [userId, pathname]);
  return recent;
}

/**
 * The sidebar (2026-10-09 left nav): wordmark and bell, a find box that filters what is below, Pinned, Recent, the
 * projects grouped Clients / Products / Platforms / Just me (collapsible, remembered), this project's pages, the
 * company links and the user card. Every project row keeps its New conversation button.
 */
export function Sidebar({ projectId, open, onMobile, openConversationId, creating, onNewConversation, closeSidebar, threadsApi }: SidebarProps) {
  const { user, projects, admin, setUser } = useSession();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { done } = useCompanion();
  const { count: mentionCount } = useMentions(user?.id, openConversationId);
  const [query, setQuery] = useState('');
  const { pinned, toggle: togglePin } = usePinned();
  const { collapsed, toggle: toggleGroup } = useCollapsed();
  const recent = useRecent(user?.id, pathname);
  const parkingCount = useParkingCount(user?.id, pathname);

  const { threads, threadsComplete, expanded, loadThreads, toggleProject, updateThread, removeThread, renameThread, archiveThread } = threadsApi;

  const all = projects as SideProject[];
  const filtering = query.trim().length > 0;
  const shown = useMemo(() => all.filter((p) => matchesQuery(query, p.kind === 'personal' ? 'My space' : p.name)), [all, query]);
  const groups = useMemo(() => groupProjects(shown), [shown]);
  const pinnedProjects = useMemo(() => pinned.map((id) => shown.find((p) => p.id === id)).filter((p): p is SideProject => !!p && inNav(p)), [pinned, shown]);
  const recentShown = recent.filter((c) => matchesQuery(query, conversationTitle(c.title), c.projectName));
  // The project list carries the caller's role (the api adds it); a guest has no saved documents to open.
  const isGuestHere = all.find((p) => p.id === projectId)?.role === 'guest';
  // Only the team makes projects; a guest (or someone with only a personal space) gets no New project links.
  const onTheTeam = all.some((p) => p.kind === 'team' && p.role !== undefined && p.role !== 'guest');
  const labelOf = (p: SideProject) => (p.kind === 'personal' ? 'My space' : p.name);

  /** One line per thread: the title carries the row; a kebab offers rename and archive; the age sits at the right edge. */
  const threadLink = (c: ThreadRow, pid: string) => {
    const unmentioned = isUnmentioned(c, openConversationId);
    const finished = done.some((d) => d.conversationId === c.id);
    return (
      <SidebarThread
        key={c.id}
        thread={c}
        projectId={pid}
        active={c.id === openConversationId}
        unmentioned={unmentioned}
        finished={finished}
        onUpdated={(conv) => updateThread(pid, conv)}
        onArchived={(conv) => {
          if (conv.status === 'archived') removeThread(pid, conv.id);
          else updateThread(pid, conv);
        }}
        onRename={renameThread}
        onArchive={archiveThread}
        closeSidebar={closeSidebar}
      />
    );
  };

  /**
   * A project row: the name toggles the recent threads (and opens the project); New conversation starts one in it
   * (the server picks the role, so guest and My space rules are the project page's); the star pins it; the chevron
   * shows or hides threads. The buttons are siblings of the link, not inside it, so they never navigate to the project
   * or toggle the row. Rows under Pinned are shortcuts: no threads, no chevron.
   */
  const projectRow = (p: SideProject, opts: { shortcut?: boolean } = {}) => {
    const label = labelOf(p);
    const isPinned = pinned.includes(p.id);
    const showThreads = !opts.shortcut && expanded.has(p.id);
    return (
      <div key={`${opts.shortcut ? 'pin' : 'row'}-${p.id}`} className={`side-project${opts.shortcut ? ' shortcut' : ''}`}>
        <NavLink
          to={`/p/${p.id}`}
          end
          className={({ isActive }) => `side-link ${p.kind === 'personal' ? 'my-space' : ''} ${isActive || p.id === projectId ? 'active' : ''}`}
          onClick={() => (opts.shortcut ? closeSidebar() : toggleProject(p.id))}
          {...(opts.shortcut ? {} : { 'aria-expanded': expanded.has(p.id) })}
          title={p.name}
        >
          <FolderIcon />
          <span className="side-link-text">{label}</span>
        </NavLink>
        <button
          type="button"
          className={`icon-btn pin-btn${isPinned ? ' on' : ''}`}
          aria-pressed={isPinned}
          aria-label={isPinned ? `Unpin ${label}` : `Pin ${label}`}
          title={isPinned ? `Unpin ${label}` : `Pin ${label}`}
          onClick={() => togglePin(p.id)}
        >
          <StarIcon filled={isPinned} />
        </button>
        <button
          type="button"
          className="icon-btn new-thread"
          disabled={creating}
          aria-label={`New conversation in ${label}`}
          title={`New conversation in ${label}`}
          onClick={() => void onNewConversation(p.id)}
        >
          <NewIcon />
        </button>
        {opts.shortcut ? null : (
          <button
            type="button"
            className={`icon-btn chev ${expanded.has(p.id) ? 'open' : ''}`}
            aria-expanded={expanded.has(p.id)}
            aria-label={`${expanded.has(p.id) ? 'Hide' : 'Show'} threads in ${label}`}
            onClick={() => toggleProject(p.id)}
          >
            <ChevronIcon />
          </button>
        )}
        {showThreads ? (
          <>
            {(threads[p.id] ?? []).length > 0 ? (
              (threads[p.id] ?? []).map((c) => threadLink(c, p.id))
            ) : (
              <div className="muted tiny side-thread-empty">No threads yet.</div>
            )}
            {(threads[p.id] ?? []).length > 0 ? (
              <button
                type="button"
                className="link-btn tiny side-show-all"
                onClick={() => loadThreads(p.id, !threadsComplete[p.id])}
              >
                {threadsComplete[p.id] ? 'Recent threads only' : 'Show all threads'}
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    );
  };

  return (
    <aside className="sidebar" aria-hidden={onMobile && !open} inert={onMobile && !open}>
      {/* Everything but the user card scrolls; the card is the sidebar's fixed footer (JB-1064). */}
      <div className="side-scroll">
      <div className="wordmark">
        <div className="row wordmark-row">
          <Link to="/" onClick={closeSidebar} className="otter-mark">
            <img src="/icon.svg" alt="" />
          </Link>
          <div className="wordmark-text">
            <Link to="/" className="wordmark-name" onClick={closeSidebar}>
              Jabberr
            </Link>
            <div className="wordmark-byline">by Tarawa Group</div>
          </div>
          {user ? (
            <span className="bell">
              <WhatsNew userId={user.id} />
              {mentionCount > 0 ? <span className="side-bell-badge">{mentionCount}</span> : null}
            </span>
          ) : null}
        </div>
      </div>

      <SidebarSearch closeSidebar={closeSidebar} query={query} setQuery={setQuery} />

      {user && !filtering ? <NavLink to="/meetings" className="side-link" onClick={closeSidebar}><PeopleIcon /><span className="side-link-text">Meetings</span></NavLink> : null}

      {user && !filtering ? (
        <NavLink to="/parking-lot" className="side-link parking-link" onClick={closeSidebar}>
          <BulbIcon />
          <span className="side-link-text">Parking lot</span>
          {parkingCount ? <span className="parking-count" aria-label={`${parkingCount} open ideas`}>{parkingCount}</span> : null}
        </NavLink>
      ) : null}

      {user && !filtering ? (
        <NavLink to="/label" className="side-link help-train" onClick={closeSidebar}>
          <TrainIcon className="side-ic" />
          Help train Jabberr
        </NavLink>
      ) : null}

      {pinnedProjects.length > 0 ? (
        <nav className="side-section" aria-label="Pinned">
          <div className="side-heading">
            <span className="side-heading-label">Pinned</span>
          </div>
          {pinnedProjects.map((p) => projectRow(p, { shortcut: true }))}
        </nav>
      ) : null}

      {recentShown.length > 0 ? (
        <nav className="side-section" aria-label="Recent">
          <div className="side-heading">
            <span className="side-heading-label">Recent</span>
          </div>
          {recentShown.map((c) => (
            <NavLink
              key={c.id}
              to={`/p/${c.projectId}/c/${c.id}`}
              className={({ isActive }) => `side-link conv recent-link ${isActive ? 'active' : ''}`}
              title={`${conversationTitle(c.title)} · ${c.projectKind === 'personal' ? 'My space' : c.projectName}`}
              onClick={closeSidebar}
            >
              <span className="conv-title">{conversationTitle(c.title)}</span>
              <span className="recent-code" aria-label={c.projectKind === 'personal' ? 'My space' : c.projectName}>
                {c.projectKind === 'personal' ? 'Me' : projectCode(c.projectName)}
              </span>
            </NavLink>
          ))}
        </nav>
      ) : null}

      <nav className="side-section side-groups" aria-label="Projects">
        {filtering && groups.length === 0 ? <div className="muted small side-empty">No project matches.</div> : null}
        {!filtering && all.length === 0 ? <div className="muted small side-empty">No projects yet.</div> : null}
        {groups.map(({ group, projects: members }) => {
          const isOpen = filtering || !collapsed.has(group);
          return (
            <div key={group} className="side-group">
              <button
                type="button"
                className={`side-group-head${isOpen ? ' open' : ''}`}
                aria-expanded={isOpen}
                onClick={() => toggleGroup(group)}
              >
                <ChevronIcon />
                <span>{NAV_GROUP_LABELS[group]}</span>
                {isOpen ? null : <span className="side-group-count">· {members.length}</span>}
              </button>
              {isOpen ? members.map((p) => projectRow(p)) : null}
            </div>
          );
        })}
        {filtering || !onTheTeam ? null : (
          <div className="side-new-links">
            <Link to="/projects/new" className="link-btn tiny" onClick={closeSidebar}>
              New project
            </Link>
            <Link to="/projects/new?private=1" className="link-btn tiny" onClick={closeSidebar}>
              New private project
            </Link>
          </div>
        )}
      </nav>

      {projectId && !filtering ? (
        <nav className="side-section">
          <div className="side-heading">
            <span>This project</span>
          </div>
          <NavLink to={`/p/${projectId}/knows`} className="side-link small" onClick={closeSidebar}>
            <BookIcon />
            What Jabberr knows
          </NavLink>
          {isGuestHere ? null : (
            <NavLink to={`/p/${projectId}/outputs`} className="side-link small" onClick={closeSidebar}>
              <FileIcon />
              Saved documents
            </NavLink>
          )}
        </nav>
      ) : null}

      {user && admin && !filtering ? (
        <nav className="side-section" aria-label="Company">
          <div className="side-heading">
            <span>Company</span>
          </div>
          <NavLink to="/users" className="side-link small" onClick={closeSidebar} aria-label="Users">
            <TeamIcon className="side-ic" />
            Users
          </NavLink>
        </nav>
      ) : null}
      </div>

      <div className="side-footer">
        {user ? <NavLink to="/settings" className="side-link small" onClick={closeSidebar}><CogIcon />Settings</NavLink> : null}
        {user ? <PersonalAppearance userId={user.id} /> : null}
         <span className="user-card">
            <span className="user-info">
              <span className="avatar avatar-big" aria-hidden="true">
                {initials(user?.name ?? user?.email ?? '?')}
              </span>
              <div className="user-meta">
                <div className="user-name">{user?.name ?? user?.email}</div>
                {user?.name ? <div className="muted tiny">{user.email}</div> : null}
              </div>
            </span>
            <button
              type="button"
              className="icon-btn"
              title="Sign out"
              aria-label="Sign out"
              onClick={() => {
                void signOut().finally(() => {
                  setUser(null);
                  navigate('/login');
                });
              }}
            >
              <SignOutIcon />
            </button>
            <ThemeToggle />
          </span>
      </div>
    </aside>
  );
}

interface SidebarThreadProps {
  thread: Conversation;
  projectId: string;
  active: boolean;
  unmentioned: boolean;
  finished: boolean;
  onUpdated: (conversation: Conversation) => void;
  onArchived: (conversation: Conversation) => void;
  onRename: (threadId: string, title: string) => Promise<Conversation | null>;
  onArchive: (threadId: string) => Promise<Conversation | null>;
  closeSidebar: () => void;
}

/** A sidebar thread row with a hover kebab offering rename and archive. */
function SidebarThread({ thread, projectId, active, unmentioned, finished, onUpdated, onArchived, onRename, onArchive, closeSidebar }: SidebarThreadProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const rename = async (): Promise<void> => {
    setEditing(false);
    const title = draft.trim();
    if (title === (thread.title ?? '')) return;
    const conversation = await onRename(thread.id, title);
    if (conversation) onUpdated(conversation);
  };

  const archive = async (): Promise<void> => {
    setMenuOpen(false);
    const conversation = await onArchive(thread.id);
    if (conversation) onArchived(conversation);
  };

  if (editing) {
    return (
      <div className="thread-wrap">
        <input
          autoFocus
          className="thread-rename"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void rename()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void rename();
            if (e.key === 'Escape') setEditing(false);
          }}
          aria-label="Thread title"
          maxLength={200}
        />
      </div>
    );
  }

  return (
    <div className="thread-wrap" ref={menuRef}>
      <NavLink
        to={`/p/${projectId}/c/${thread.id}`}
        className={({ isActive }) => `side-link conv thread ${isActive || active ? 'active' : ''}`}
        title={conversationTitle(thread.title)}
        onClick={closeSidebar}
      >
        {unmentioned ? <span className="mention-dot" role="img" aria-label="Someone mentioned you here" /> : null}
        {finished ? <span className="done-dot" role="img" aria-label="A reply finished in this thread" /> : null}
        <span className="conv-title">{conversationTitle(thread.title)}</span>
        <span className="muted tiny conv-age">{relativeTime(thread.updatedAt)}</span>
      </NavLink>
      <button
        type="button"
        className="icon-btn thread-kebab"
        aria-label={`Thread options for ${conversationTitle(thread.title)}`}
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((o) => !o)}
      >
        <KebabSmallIcon />
      </button>
      {menuOpen ? (
        <div className="thread-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="menu-item small"
            onClick={() => {
              setMenuOpen(false);
              setDraft(thread.title ?? '');
              setEditing(true);
            }}
          >
            Rename
          </button>
          <button type="button" role="menuitem" className="menu-item small" onClick={() => void archive()}>
            Archive
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ThemeToggle() {
  const [theme, setTheme] = useState(() => getTheme());
  const cycle = useCallback(() => {
    const next = cycleTheme();
    setTheme(next);
  }, []);
  const current = theme;
  const next = getNextTheme(current);
  const label = `Theme: ${getThemeLabel(current)}. Switch to ${getThemeLabel(next)}`;

  return (
    <button
      type="button"
      className="icon-btn"
      aria-label={label}
      onClick={cycle}
      title={label}
    >
      {current === 'light' ? <SunIcon /> : current === 'dark' ? <MoonIcon /> : <SystemIcon />}
    </button>
  );
}
